import { downloadBlob } from '@mint-lib/api-zod-support';
import { useTranslation } from '@mint-lib/i18n';
import { Download } from '@mint-lib/icons';
import { useFlags } from '@mint-lib/routing-context';
import { Button, typographyVariants } from '@mint-lib/tw-components';
import { Chat } from '@mint-sdk/api-ai-assistant';

import { useAiHubPermissions } from '../../hooks/useAiHubPermissions.jsx';
import { AiDataGrid } from './AiDataGrid.jsx';
import { AiDataTable } from './AiDataTable.jsx';

type Props = {
  dataframeData: Chat.AiChatMessageRecordType['message']['additionalMetadata']['dataframe'];
  chatId: string;
  messageId: string;
  type?: 'mobile' | 'desktop' | 'agentic';
  tableName?: string;
};

const DataTable = ({
  dataframeData,
  chatId,
  messageId,
  type = 'agentic',
  tableName,
}: Props) => {
  const ff = useFlags('@mint/arm', ['aiScrollableTables']);
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { canDownloadTable } = useAiHubPermissions();

  const isDownloadEnabled = type !== 'mobile';
  const hasHeader = tableName || isDownloadEnabled;

  const DataTableContent = ff.aiScrollableTables.enabled
    ? AiDataGrid
    : AiDataTable;

  const onDownloadTable = async () => {
    const data = await Chat.getTable({
      urlParams: {
        chatSessionId: chatId,
        messageId: messageId,
      },
    });

    downloadBlob(data, 'chat-table');
  };

  return (
    <div className="flex flex-col gap-4 pt-0 pb-2" data-testid="ai-data-table">
      {hasHeader && (
        <div className="flex flex-row">
          {!!tableName && (
            <div className="flex flex-1 justify-start">
              <p
                className={typographyVariants({
                  variant: 'h4',
                })}
              >
                {tableName}
              </p>
            </div>
          )}

          {isDownloadEnabled && (
            <div className="flex flex-1 justify-end">
              <Button
                aria-label="Download table"
                onClick={onDownloadTable}
                size="small"
                variant="outlined"
                className="self-end"
                disabled={!canDownloadTable}
              >
                <Download />
                {t('Download')}
              </Button>
            </div>
          )}
        </div>
      )}
      <DataTableContent dataframeData={dataframeData} type={type} />
    </div>
  );
};

export { DataTable };
